import { useState, useEffect } from "react";
import { Banknote, Smartphone, CreditCard, CheckCircle2, XCircle, Save, Loader } from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";

const ALL_METHODS = [
  { v: "efectivo",  l: "Efectivo",  desc: "Pago en efectivo al recibir",        icon: Banknote,   color: "green"  },
  { v: "nequi",     l: "Nequi",     desc: "Billetera digital Nequi",            icon: Smartphone, color: "violet" },
  { v: "daviplata", l: "Daviplata", desc: "Billetera digital Daviplata",        icon: Smartphone, color: "red"    },
  { v: "tarjeta",   l: "Tarjeta",   desc: "Tarjeta débito / crédito",           icon: CreditCard, color: "blue"   },
];

const COLOR = {
  green:  "border-green-500  bg-green-50  text-green-700",
  violet: "border-violet-500 bg-violet-50 text-violet-700",
  red:    "border-red-500    bg-red-50    text-red-700",
  blue:   "border-blue-500   bg-blue-50   text-blue-700",
};

export default function PaymentMethodsManager() {
  const [enabled, setEnabled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    API.get("/settings/payment-methods")
      .then((res) => setEnabled(res.data?.data?.methods ?? ["efectivo"]))
      .catch(() => setEnabled(["efectivo"]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (v) => {
    setEnabled((prev) =>
      prev.includes(v)
        ? prev.filter((m) => m !== v)
        : [...prev, v]
    );
  };

  const handleSave = async () => {
    if (enabled.length === 0) {
      toast.error("Debes habilitar al menos un método de pago");
      return;
    }
    setSaving(true);
    try {
      await API.put("/settings/payment-methods", { methods: enabled });
      toast.success("Métodos de pago actualizados");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-supernova-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-gray-800">Métodos de pago</h2>
        <p className="text-sm text-gray-500 mt-1">
          Activa o desactiva los métodos disponibles para los clientes en el checkout.
        </p>
      </div>

      <div className="space-y-3 mb-8">
        {ALL_METHODS.map((m) => {
          const active = enabled.includes(m.v);
          const Icon = m.icon;
          return (
            <button
              key={m.v}
              onClick={() => toggle(m.v)}
              className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left
                ${active ? COLOR[m.color] : "border-gray-100 bg-white text-gray-500 hover:border-gray-200"}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0
                ${active ? "bg-white/60" : "bg-gray-100"}`}>
                <Icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{m.l}</p>
                <p className={`text-xs mt-0.5 ${active ? "opacity-70" : "text-gray-400"}`}>{m.desc}</p>
              </div>
              {active
                ? <CheckCircle2 size={20} className="shrink-0" />
                : <XCircle     size={20} className="shrink-0 text-gray-300" />
              }
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || enabled.length === 0}
          className="flex items-center gap-2 px-6 py-2.5 bg-supernova-500 hover:bg-supernova-600 disabled:opacity-50 text-white font-semibold text-sm rounded-xl transition"
        >
          {saving ? <Loader size={15} className="animate-spin" /> : <Save size={15} />}
          Guardar cambios
        </button>
        <span className="text-xs text-gray-400">
          {enabled.length} de {ALL_METHODS.length} habilitados
        </span>
      </div>
    </div>
  );
}
